import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobalState } from '../../utils/GlobalState';
import { ArrowLeft } from "lucide-react";
import { createMuraLabel, fetchIssueTypes } from "../../services/apiService";
import Swal from "sweetalert2";
import { decodedToken, getAuthToken } from "../../utils/auth";

const generateRectHash = (rect) => {
  return `${rect.muraType}|${rect.x.toFixed(2)}|${rect.y.toFixed(2)}|${rect.width.toFixed(2)}|${rect.height.toFixed(2)}`;
};


const getUserType = () => {
  try {
    const token = getAuthToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType?.toLowerCase() ?? null;
  } catch {
    return null;
  }
};


const MuraLabeling = () => {
  const isPowerAdmin = getUserType() === "poweradmin";
  const location = useLocation();
  const navigate = useNavigate();
  const { state, updateState } = useGlobalState();
  const { editingProductId, recordedEntries: initialRecordedEntries, imageDimensions } = state || {};

  const [selectedMuraType, setSelectedMuraType] = useState("");
  const [customMuraType, setCustomMuraType] = useState("");
  const [customMuraTypes, setCustomMuraTypes] = useState({});
  const [recordedEntries, setRecordedEntries] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomMuraTypeValid, setIsCustomMuraTypeValid] = useState(true);
  const [muraTypes, setMuraTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const uploadedImage = state.uploadedImage || location.state?.uploadedImage || "";
  const incomingMuraType = location.state?.muraType || "";
  const incomingCustomMuraType = location.state?.customMuraType || "";
  const incomingCauses = location.state?.selectedCauses || [];
  const incomingIssues = location.state?.selectedIssues || [];
  const fromLightLeakage = location.state?.fromLightLeakage || false;
  const incomingMuraTypeNormalized = incomingMuraType.toLowerCase().replace(/_/g, ' ');
  const issueType = location.state?.issueType || (incomingMuraTypeNormalized === "pressing light leakage" ? "LightLeakage" : "MuraLabeling");
  const productInfo = state.formData && state.formData.length > 0 ? state.formData[state.formData.length - 1] : null;


  const getNextMuraTypeId = () => {
    const existingIds = [
      ...muraTypes.map(type => parseInt(type.muraLabId)),
      ...Object.values(customMuraTypes).map(id => parseInt(id)),
    ];
    return Math.max(...existingIds, 0) + 1;
  };


  useEffect(() => {
    const loadIssueTypes = async () => {
      try {
        setIsLoading(true);
        const response = await fetchIssueTypes();
        const types = response.data.map(type => ({
          muraType: type.muraType.replace(/_/g, ' '),
          muraLabId: type.muraLabId,
        }));
        setMuraTypes(types);
      } catch (err) {
        console.error('Failed to load issue types:', err.message);
        Swal.fire({
          icon: "error",
          title: "Failed to Load Issue Types",
          text: `Failed to load issue types: ${err.message}`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadIssueTypes();
  }, []);

  useEffect(() => {
    if (!hasInitialized) {
      let initialEntries = [];
      if (location.state?.keepState) {
        initialEntries = (location.state.recordedEntries || []).filter(
          entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
        ).map(entry => {
          const { _id, ...cleanEntry } = entry;
          return cleanEntry;
        });
        setSelectedMuraType(location.state.selectedMuraType || "");
        setCustomMuraType(location.state.customMuraType || "");
      } else {
        if (location.state?.recordedEntries) {
          initialEntries = location.state.recordedEntries.filter(
            entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
          ).map(entry => {
            const { _id, ...cleanEntry } = entry;
            return cleanEntry;
          });
        } else {
          const storedEntries = localStorage.getItem("recordedEntries");
          if (storedEntries) {
            try {
              const parsedEntries = JSON.parse(storedEntries);
              initialEntries = parsedEntries.filter(
                entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
              ).map(entry => {
                const { _id, ...cleanEntry } = entry;
                return cleanEntry;
              });
            } catch (e) {
              console.error("Failed to parse recordedEntries", e);
              Swal.fire({
                icon: "error",
                title: "Data Parsing Error",
                text: "Failed to parse recorded entries from storage. Clearing invalid data.",
                confirmButtonColor: "#2563eb",
                confirmButtonText: "OK",
              });
              localStorage.removeItem("recordedEntries");
            }
          }
        }
      }

      setRecordedEntries(initialEntries);

      if (incomingMuraType) {
        setSelectedMuraType(incomingMuraType);
      } else if (incomingCustomMuraType) {
        setCustomMuraType(incomingCustomMuraType);
        setSelectedMuraType("Other");
      }

      setHasInitialized(true);
    }
  }, [location.state, hasInitialized, incomingMuraType, incomingCustomMuraType]);

  useEffect(() => {
    localStorage.setItem("selectedMuraType", JSON.stringify(selectedMuraType));
  }, [selectedMuraType]);

  useEffect(() => {
    localStorage.setItem("recordedEntries", JSON.stringify(recordedEntries));
  }, [recordedEntries]);

  useEffect(() => {
    if ((incomingMuraType || incomingCustomMuraType) && location.state?.fromRootCauseDetail) {
      if (!incomingCauses.length) {
        Swal.fire({
          icon: "error",
          title: "No Root Cause Selected",
          text: "Please select a root cause before recording an issue type.",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        return;
      }

      const typeToRecord = incomingCustomMuraType || incomingMuraType;
      if (!typeToRecord || !incomingCauses[0]) {
        console.error("Invalid issue type or root cause:", { typeToRecord, rootCause: incomingCauses[0] });
        return;
      }

      const formattedMuraType = typeToRecord.toLowerCase().replace(/\s+/g, "_");
      const formattedCause = incomingCauses[0].toLowerCase().replace(/\s+/g, "_");

      let rectangles = [];
      try {
        const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
        if (muraData.rectangles && Array.isArray(muraData.rectangles)) {
          rectangles = muraData.rectangles
            .filter(rect => rect.muraType === formattedMuraType)
            .map(rect => {
              const { _id, ...cleanRect } = rect;
              return {
                ...cleanRect,
                rectangleId: rect.rectangleId || `${formattedMuraType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              };
            });
        }
      } catch (e) {
        console.warn("Failed to parse muraData for rectangles:", e);
      }

      let newEntries = [];
      if (fromLightLeakage) {
        newEntries = rectangles.map(rect => ({
          muraType: formattedMuraType,
          rectangle: { ...rect },
          rootCause: formattedCause,
          rootIssues: [], // Use array for multiple issues
          rectangleId: rect.rectangleId,
        }));
      } else {
        newEntries = rectangles.map(rect => ({
          muraType: formattedMuraType,
          rectangle: { ...rect },
          rootCause: formattedCause,
          rootIssues: incomingIssues.length > 0
            ? incomingIssues.map(issue => issue.toLowerCase().replace(/\s+/g, "_"))
            : [],
          rectangleId: rect.rectangleId,
        }));
      }

      setRecordedEntries(prev => [...prev, ...newEntries]);
      let muraData = {};
      try {
        muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
      } catch (e) {
        console.warn("Failed to parse muraData:", e);
      }

      const updatedMuraData = {
        ...muraData,
        muraType: formattedMuraType,
        rectangles: muraData.rectangles || [],
        imageDimensions: muraData.imageDimensions || { width: 0, height: 0 },
        yoloAnnotations: muraData.yoloAnnotations || [],
        canvasTransform: muraData.canvasTransform || {},
        processedRectHashes: muraData.processedRectHashes || [],
      };
      localStorage.setItem("muraData", JSON.stringify(updatedMuraData));
      navigate("/muralabeling", {
        state: {
          ...location.state,
          muraType: undefined,
          customMuraType: undefined,
          selectedCauses: undefined,
          selectedIssues: undefined,
          uploadedImage,
          recordedEntries: [...recordedEntries, ...newEntries],
          selectedMuraType: "",
          fromLightLeakage: undefined,
          issueType,
          yoloAnnotations: location.state?.yoloAnnotations,
          rectangles: updatedMuraData.rectangles,
        },
        replace: true,
      });

      setSelectedMuraType("");
      setCustomMuraType("");
    }
  }, [
    incomingMuraType,
    incomingCustomMuraType,
    incomingCauses,
    incomingIssues,
    fromLightLeakage,
    navigate,
    recordedEntries,
    selectedMuraType,
    uploadedImage,
    issueType,
    location.state,
  ]);

   const handleCustomMuraTypeChange = (e) => {
    const value = e.target.value;
    const invalidWords = ["other", "others", "na", "unknown", "none", "etc", "nk", "undefined", "null", "blank"];
    const validInputRegex = /^[a-zA-Z\s]*$/; 

    if (!validInputRegex.test(value)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Custom Issue Type",
        text: "The issue type can only contain letters and spaces. Special characters and numbers are not allowed.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      setIsCustomMuraTypeValid(false); 
      return;
    }

    if (invalidWords.includes(value.trim().toLowerCase())) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Custom Issue Type",
        text: `The issue type "${value.trim()}" is not allowed. Please enter a valid custom issue type.`,
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      setIsCustomMuraTypeValid(false); 
      return;
    }

    if (muraTypes.some(type => type.muraType.toLowerCase() === value.trim().toLowerCase())) {
      Swal.fire({
        icon: "warning",
        title: "Issue Type Exists",
        text: `The issue type "${value.trim()}" already exists in the list. Please select it from the available options.`,
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      setIsCustomMuraTypeValid(false);
      return;
    }
    setCustomMuraType(value);
    setIsCustomMuraTypeValid(true); 
    if (value.trim() && selectedMuraType !== "Other") {
      setSelectedMuraType("Other");
    }
    if (!value.trim() && selectedMuraType === "Other") {
      setSelectedMuraType("");
      setIsCustomMuraTypeValid(true);
    }
  };

  const handleCustomMuraTypeFocus = () => {
    if (selectedMuraType !== "Other") {
      setSelectedMuraType("Other");
    }
  };

  const handleMuraTypeSelect = (type) => {
    if (selectedMuraType === type) {
      setSelectedMuraType("");
      if (type === "Other") {
        setCustomMuraType("");
      }
    } else {
      setSelectedMuraType(type);
      if (type !== "Other") {
        setCustomMuraType("");
      }
    }
  };

  const handleLabeling = () => {
    if (!selectedMuraType) {
      Swal.fire({
        icon: "error",
        title: "No Mura Type Selected",
        text: "Please select a issue Type from the list.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    if (selectedMuraType === "Other" && !customMuraType.trim()) {
      Swal.fire({
        icon: "error",
        title: "Custom Mura Type Required",
        text: "Please specify a custom issue type when selecting 'Other'.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    const muraTypeToPass = selectedMuraType === "Other" ? customMuraType : selectedMuraType;
    const muraTypeNormalized = muraTypeToPass.toLowerCase().trim().replace(/\s+/g, " ");
    const computedIssueType = muraTypeNormalized === "pressing light leakage" ? "LightLeakage" : "MuraLabeling";
    const formattedMuraType = muraTypeNormalized.replace(/\s+/g, "_");
    const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");

    if (selectedMuraType === "Other") {
      setCustomMuraTypes(prev => ({
        ...prev,
        [formattedMuraType]: prev[formattedMuraType] || String(getNextMuraTypeId()),
      }));
    }
    const filteredRecordedEntries = recordedEntries.filter(
      entry => entry.muraType === formattedMuraType && entry.rootCause && entry.rootIssue
    );

    navigate("/mark-defect-region", {
      state: {
        uploadedImage,
        muraType: selectedMuraType === "Other" ? "" : muraTypeToPass,
        customMuraType: selectedMuraType === "Other" ? customMuraType.trim() : "",
        recordedEntries,
        selectedMuraType,
        fromMuraLabeling: true,
        issueType: computedIssueType,
        yoloAnnotations: location.state?.yoloAnnotations || [],
        muraData,
        imageDimensions: state.imageDimensions || {},
        customMuraTypes: {
          ...muraTypes.reduce((acc, type) => ({
            ...acc,
            [type.muraType.toLowerCase().replace(/\s+/g, "_")]: type.muraLabId,
          }), {}),
          ...customMuraTypes,
        },
      },
    });
  };


  const hasRecordingData = !!(selectedMuraType || customMuraType || recordedEntries.length);

  const handleClear = async () => {
    const result = await Swal.fire({
      title: "Clear All Labelings?",
      text: "This will remove all your Issue type selections and recordings, but keep the uploaded image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear it!",
    });

    if (!result.isConfirmed) return;

    setSelectedMuraType("");
    setCustomMuraType("");
    setRecordedEntries([]);

    const clearItems = [
      "recordedEntries",
      "selectedMuraType",
      "muraData",
      "muraRectangles",
    ];
    clearItems.forEach((item) => localStorage.removeItem(item));

    navigate("/muralabeling", {
      replace: true,
      state: {
        uploadedImage,
        issueType,
        imageDimensions: state.imageDimensions || {},
      },
    });
  };

const handleSubmit = async () => {
  if (!recordedEntries.length) {
    Swal.fire({
      icon: "error",
      title: "No Data",
      text: "Please add at least one issue Type labeling before submission.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    return;
  }

  if (recordedEntries.some((entry) => !entry.rootCause)) {
    Swal.fire({
      icon: "error",
      title: "Missing Data",
      text: "All recorded entries must have a root cause before submission.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    return;
  }

  //if (!productInfo || !productInfo.productType || !productInfo.modelName || !productInfo.brand) {
  if (!productInfo || !productInfo.productType || !productInfo.modelName) {
    Swal.fire({
      icon: "error",
      title: "Missing Product Info",
      text: "Product information (type, brand, and model name) is required.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    return;
  }

  if (productInfo.productType !== 'PD' && !productInfo.hasOwnProperty('bezel')) {
    Swal.fire({
      icon: "error",
      title: "Missing Bezel Information",
      text: "Bezel information is required for non-PD product types.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    return;
  }

  setIsSubmitting(true);

  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing. Please log in again.");
    }

    const decoded = decodedToken();
    if (!decoded || !decoded.email) {
      throw new Error("Invalid or expired token. Please log in again.");
    }

    const companyInfo = decoded.companyInfo || localStorage.getItem('companyInfo') || 'Unknown';
    const email = decoded.email;

    // Validate companyInfo
    if (!companyInfo || companyInfo === 'Unknown') {
      console.warn("companyInfo is 'Unknown' or missing, which may cause backend validation to fail.");
    }

    let muraData;
    try {
      muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
    } catch (e) {
      throw new Error("Invalid muraData format in localStorage.");
    }

    if (!muraData.rectangles || !Array.isArray(muraData.rectangles) || !muraData.rectangles.length) {
      throw new Error("No valid rectangles found in muraData.");
    }

    let imageWidth = muraData.imageDimensions?.width;
    let imageHeight = muraData.imageDimensions?.height;

    if (!imageWidth || !imageHeight || !Number.isFinite(imageWidth) || !Number.isFinite(imageHeight)) {
      if (!uploadedImage) {
        throw new Error("No uploaded image provided for dimensions calculation.");
      }
      const img = new Image();
      img.src = uploadedImage;
      await new Promise((resolve, reject) => {
        img.onload = () => {
          imageWidth = img.naturalWidth;
          imageHeight = img.naturalHeight;
          muraData.imageDimensions = { width: imageWidth, height: imageHeight };
          localStorage.setItem('muraData', JSON.stringify(muraData));
          if (
            state.imageDimensions?.width !== imageWidth ||
            state.imageDimensions?.height !== imageHeight
          ) {
            updateState({ imageDimensions: { width: imageWidth, height: imageHeight } });
          }
          resolve();
        };
        img.onerror = () => reject(new Error('Failed to load image for dimensions'));
      });
    }

    const finalImageWidth = state.imageDimensions?.width && Number.isFinite(state.imageDimensions.width)
      ? state.imageDimensions.width
      : imageWidth;
    const finalImageHeight = state.imageDimensions?.height && Number.isFinite(state.imageDimensions.height)
      ? state.imageDimensions.height
      : imageHeight;

    if (!Number.isFinite(finalImageWidth) || !Number.isFinite(finalImageHeight) || finalImageWidth <= 0 || finalImageHeight <= 0) {
      throw new Error(`Invalid image dimensions: width=${finalImageWidth}, height=${finalImageHeight}`);
    }

    const seenRectHashes = new Set();
    muraData.rectangles = muraData.rectangles.filter((rect, idx) => {
      if (
        !Number.isFinite(rect.x) ||
        !Number.isFinite(rect.y) ||
        !Number.isFinite(rect.width) ||
        !Number.isFinite(rect.height) ||
        rect.width <= 0 ||
        rect.height <= 0
      ) {
        console.warn(`Invalid rectangle at index ${idx}:`, rect);
        return false;
      }
      const hash = generateRectHash(rect);
      if (seenRectHashes.has(hash)) {
        console.warn(`Duplicate rectangle detected at index ${idx}:`, rect);
        return false;
      }
      seenRectHashes.add(hash);
      delete rect._id;
      return true;
    });

    if (!muraData.rectangles.length) {
      throw new Error("No valid rectangles after validation.");
    }

    const entryMap = recordedEntries.reduce((acc, entry, index) => {
      const muraTypeLower = entry.muraType.toLowerCase().replace(/\s+/g, "_");
      if (!acc[muraTypeLower]) {
        acc[muraTypeLower] = [];
      }
      const { _id, ...cleanEntry } = entry;
      acc[muraTypeLower].push({ ...cleanEntry, originalIndex: index });
      return acc;
    }, {});

    const rectangleCounts = muraData.rectangles.reduce((acc, rect) => {
      const muraTypeLower = (rect.muraType || customMuraType || "other")
        .toLowerCase()
        .replace(/\s+/g, "_");
      acc[muraTypeLower] = (acc[muraTypeLower] || 0) + 1;
      return acc;
    }, {});

    for (const [muraType, rectCount] of Object.entries(rectangleCounts)) {
      const entries = entryMap[muraType] || [];
      if (!entries.length) {
        throw new Error(`No recorded entries for mura type: ${muraType.replace(/_/g, ' ')}.`);
      }
      if (rectCount > entries.length) {
        throw new Error(`Too many rectangles for ${muraType.replace(/_/g, ' ')}: ${rectCount} rectangles but only ${entries.length} entries.`);
      }
    }

    const defects = [];
    const usedEntryIndices = new Set();
    for (const rect of muraData.rectangles) {
      const muraTypeLower = (rect.muraType || customMuraType || "other")
        .toLowerCase()
        .replace(/\s+/g, "_");
      const xCenter = (rect.x + rect.width / 2) / finalImageWidth;
      const yCenter = (rect.y + rect.height / 2) / finalImageHeight;
      const width = rect.width / finalImageWidth;
      const height = rect.height / finalImageHeight;

      const bbox = [xCenter, yCenter, width, height];

      const clampedBbox = bbox.map((coord, i) => {
        if (!Number.isFinite(coord)) {
          console.warn(`Defect bbox[${i}] is not finite: ${coord}`);
          return 0;
        }
        return Math.max(0, Math.min(1, coord));
      });

      if (clampedBbox.some((coord) => !Number.isFinite(coord))) {
        throw new Error(`Invalid bbox for rectangle: coordinates must be finite.`);
      }

      const matchingEntries = entryMap[muraTypeLower] || [];
      let entry = matchingEntries.find(
        (e) => !usedEntryIndices.has(e.originalIndex)
      );

      if (!entry) {
        throw new Error(`No available recorded entry for muraType: ${muraTypeLower}.`);
      }

      usedEntryIndices.add(entry.originalIndex);

      if (!entry.rootCause) {
        throw new Error(`Root cause is required for defect.`);
      }

      const defect = {
        muraType: muraTypeLower,
        muraLabelIssue: {
          muraRootCause: entry.rootCause,
          muraRootIssue: entry.rootIssues || [],
        },
        bbox: clampedBbox,
        muraLabId: entry.originalIndex + 1,
      };

      defects.push(defect);
    }

    // Validate uploadedImage before base64ToFile
    if (!uploadedImage || typeof uploadedImage !== 'string' || !uploadedImage.startsWith('data:image/')) {
      throw new Error("Invalid or missing uploaded image data.");
    }

    const base64ToFile = (base64, filename) => {
      try {
        const arr = base64.split(",");
        if (!arr[0].match(/data:image\/(png|jpeg|jpg)/)) {
          throw new Error("Invalid image format: must be PNG or JPEG.");
        }
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      } catch (e) {
        throw new Error(`Failed to convert base64 image: ${e.message}`);
      }
    };

    const imageFile = base64ToFile(uploadedImage, `image_${issueType === "LightLeakage" ? "LL" : "muralabel"}_${Date.now()}.png`);
    let brand = productInfo?.brand?.trim();
    // Prepare productInfo and clean up undefined values
    const cleanedProductInfo = {
      productType: productInfo.productType,
      modelName: productInfo.modelName,
     // brand: productInfo.brand,
      brand,
      bezel: productInfo.productType !== 'PD' ? (productInfo.bezel || 'no') : undefined,
      dimensions: productInfo.dimensions || null,
      screenType: productInfo.screenType || null,
      inchSize: productInfo.inchSize || null,
      companyInfo: companyInfo || null,
    };

    // Remove undefined fields to avoid JSON.stringify issues
    Object.keys(cleanedProductInfo).forEach(key => {
      if (cleanedProductInfo[key] === undefined) {
        delete cleanedProductInfo[key];
      }
    });

    const formData = new FormData();
    formData.append("issueType", issueType || "MuraLabeling");
    formData.append("email", email);
    formData.append("companyInfo", companyInfo);
    formData.append("productInfo", JSON.stringify(cleanedProductInfo));
    const muraLabelingPayload = { defects };
    formData.append("muraLabeling", JSON.stringify(muraLabelingPayload));
    formData.append("rawimage", imageFile);
    if (editingProductId) {
      formData.append("editingProductId", editingProductId);
    }
    // Set dataFromStatus: use state.dataFromStatus if present (e.g., for relabeling), otherwise default to "labeling"
      const dataFromStatus = state.dataFromStatus || "labeling";
      formData.append("dataFromStatus", dataFromStatus);

    // Log formData contents for debugging
    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    await createMuraLabel(formData);

    const clearItems = [
      "recordedEntries",
      "selectedMuraType",
      "muraData",
      "muraRectangles",
    ];
    clearItems.forEach((item) => localStorage.removeItem(item));

    setSelectedMuraType("");
    setCustomMuraType("");
    setRecordedEntries([]);
    setIsCustomMuraTypeValid(true);
    updateState({ formData: [], uploadedImage: null, editingProductId: null, imageDimensions: null });

    await Swal.fire({
      icon: "success",
      title: "Success!",
      text: editingProductId ? "Relabeling submitted successfully." : "Data successfully submitted.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });

    navigate("/mura-labeling", {
      state: { fromMuraLabeling: true, resubmitted: !!editingProductId },
      replace: true,
    });
  } catch (error) {
    console.error("Submission error:", error.message, error.stack);
    Swal.fire({
      icon: "error",
      title: "Submission Failed",
      text: error.message || "Failed to submit Mura labeling. Please try again.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    if (error.message.includes("token")) {
      navigate("/login", { state: { error: error.message } });
    }
  } finally {
    setIsSubmitting(false);
  }
};

  const handleBackNavigation = () => {
    const canvas = document.getElementById("defectCanvas");
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const clearItems = [
      "muraRectangles",
      "muraData",
      "recordedEntries",
      "selectedMuraType",
    ];
    clearItems.forEach((item) => localStorage.removeItem(item));

    setSelectedMuraType("");
    setCustomMuraType("");
    setRecordedEntries([]);
    setIsCustomMuraTypeValid(true);

    navigate("/upload-image", {
      state: {
        uploadedImage,
        issueType,
        imageDimensions: state.imageDimensions || {},
      },
      replace: true,
    });
  };

  return (
    <div className="p-3 px-4 py-1 bg-black text-white min-h-screen font-sans text-left md:text-center">
      <div className="flex items-center mb-4">
        <ArrowLeft
          className="w-6 h-6 cursor-pointer mr-4 md:absolute md:left-3"
          onClick={handleBackNavigation}
        />
        <h2 className="flex-1 text-xl sm:text-2xl text-left md:text-center font-semibold">
          LL/Mura/Spot/Line/Band Issue Labeling
        </h2>
      </div>

      <div className="text-sm py-1 px-0 text-[#E0E0E0] ml-0 md:ml-4">
        Select a Mura type to identify symptoms. You will be guided to record root causes on the next page. Repeat until all defects are recorded, then hit SUBMIT.
      </div>

      <div className="mt-6 text-sm sm:text-xl font-semibold mb-4">Issue Type</div>
      {isLoading ? (
        <div className="flex justify-center items-center mb-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 md:gap-2 mb-4 items-center justify-start md:justify-center">
          {muraTypes.map(({ muraType }) => {
            const formattedType = muraType.toLowerCase().replace(/\s+/g, "_");
            if (formattedType === "other" && !isPowerAdmin) return null;
            const isRecorded = recordedEntries.some((entry) => entry.muraType === formattedType);
            const isSelected = selectedMuraType === muraType;

            let bgColor;
            if (isSelected) {
              bgColor = "bg-blue-600";
            } else if (isRecorded) {
              bgColor = "bg-blue-400";
            } else {
              bgColor = "bg-[#1E2A44]";
            }

            return (
              <button
                key={muraType}
                onClick={() => handleMuraTypeSelect(muraType)}
                className={`h-[28px] sm:h-[30px] md:h-[32px] py-1 px-3 rounded-lg cursor-pointer text-[13px] min-w-[80px] sm:min-w-[100px] md:min-w-[120px] text-center ${bgColor} ${
                  isSelected ? "ring-2 ring-white" : ""
                }`}
              >
                {muraType}
              </button>
            );
          })}
        </div>
      )}

      {isPowerAdmin && (
        <div className="flex items-center mt-2 justify-start md:justify-center">
          <span className="text-white text-sm sm:text-base inline-block py-2">Other</span>
          <input
            type="text"
            value={customMuraType}
            onChange={handleCustomMuraTypeChange}
            onFocus={handleCustomMuraTypeFocus}
            placeholder="Please specify"
            className="flex-1 p-2 min-h-[30px] ml-1 rounded-lg border-none bg-[#1E2A44] text-white text-sm box-border"
          />
        </div>
      )}

      <button
        onClick={handleLabeling}
        className={`py-2 px-3 rounded-lg text-sm w-full max-w-[150px] my-6 ml-auto block mt-3 ${
          (selectedMuraType && (selectedMuraType !== "Other" || customMuraType.trim()))
            ? "bg-blue-600 text-white cursor-pointer"
            : "bg-gray-500 text-gray-300 cursor-not-allowed"
        }`}
        disabled={!selectedMuraType || (selectedMuraType === "Other" && !customMuraType.trim())}
      >
        Labeling
      </button>

      <div className="mt-6 mb-2 text-sm sm:text-xl font-semibold">Labeling Recording</div>
      <textarea
        value={Object.entries(
          recordedEntries.reduce((acc, entry) => {
            if (
              entry &&
              typeof entry.muraType === 'string' &&
              typeof entry.rootCause === 'string'
            ) {
              const muraType = entry.muraType.replace(/_/g, ' ');
              const rootCause = entry.rootCause.replace(/_/g, ' ');
              const key = `${muraType}:${rootCause}`;
              if (!acc[key]) {
                acc[key] = { muraType, rootCause, rootIssues: [] };
              }
              if (Array.isArray(entry.rootIssues)) {
                entry.rootIssues.forEach(issue => {
                  const formattedIssue = issue.replace(/_/g, ' ');
                  if (!acc[key].rootIssues.includes(formattedIssue)) {
                    acc[key].rootIssues.push(formattedIssue);
                  }
                });
              }
            }
            return acc;
          }, {})
        )
          .map(([_, { muraType, rootCause, rootIssues }]) => {
            const issues = rootIssues.length > 0 ? rootIssues.join(',  ') : ' ';
            return issues ? `${muraType }: ${   rootCause  }: ${  issues   }` : `${   muraType  }: ${  rootCause}`;
          })
          .join('\n')}
        readOnly
        placeholder="Your recorded entries will appear here"
        className="w-full p-4 my-2 rounded-lg border-none bg-[#1E2A44] text-white text-xs md:text-sm box-border resize-none min-h-[150px]"
      />

      <div className="flex justify-between mt-10">
        <button
          onClick={handleClear}
          className={`bg-blue-600 px-4 py-1.5 rounded-md w-[70px] sm:w-[100px] md:w-[400px] lg:w-[600px] xl:w-[660px] text-sm sm:text-base ${
            hasRecordingData ? "cursor-pointer" : "bg-gray-500 cursor-not-allowed"
          }`}
          disabled={!hasRecordingData}
        >
          Clear
        </button>
        <button
          onClick={handleSubmit}
          className={`bg-blue-600 px-4 py-1.5 rounded-md w-[70px] sm:w-[100px] md:w-[400px] lg:w-[600px] xl:w-[660px] text-sm sm:text-base ${
            recordedEntries.length > 0 && !isSubmitting
              ? "cursor-pointer"
              : "bg-gray-500 cursor-not-allowed"
          }`}
          disabled={recordedEntries.length === 0 || isSubmitting}
        >
          {isSubmitting ? "Submit..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default MuraLabeling;